import { useMemo } from 'react'
import styled from 'styled-components'
import { MdInfoOutline } from 'react-icons/md'
import { useDroppable, useDraggable, useDndContext } from '@dnd-kit/core'
import { computeUnavailableZones } from '../../hooks/useWorkPlanner'
import useTranslation from '../../hooks/useTranslation'

const SLOT_WIDTH  = 14   // px per 5-minute slot (~2.8 px/min, matching previous 80px/30min density)
const ROW_HEIGHT  = 64   // px
const LABEL_WIDTH = 200  // px

// ── Styled components ──────────────────────────────────────────────────────

const Row = styled.div`
    display: flex;
    align-items: stretch;
    border-bottom: 1px solid ${({ theme }) => theme.area.lightBorder};
    min-height: ${ROW_HEIGHT}px;
    flex-shrink: 0;
`

const SwimlaneLabel = styled.div`
    width: ${LABEL_WIDTH}px;
    min-width: ${LABEL_WIDTH}px;
    padding: 0 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    border-right: 1px solid ${({ theme }) => theme.area.lightBorder};
    background: ${({ theme }) => theme.card.background};
    flex-shrink: 0;
`

const WcName = styled.span`
    font-size: var(--font-S);
    font-weight: 600;
    color: ${({ theme }) => theme.font.color};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const WcMeta = styled.span`
    font-size: var(--font-XS);
    color: ${({ theme }) => theme.font.label};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const GridArea = styled.div`
    position: relative;
    display: flex;
`

const CellWrapper = styled.div`
    display: flex;
    position: relative;
    z-index: 0;
`

const Cell = styled.div`
    width: ${SLOT_WIDTH}px;
    min-width: ${SLOT_WIDTH}px;
    height: ${ROW_HEIGHT}px;
    border-left: 1px solid ${({ $isHour, $isFirst, theme }) => ($isHour && !$isFirst) ? theme.area.lightBorder : 'transparent'};
    background: ${({ $isOver, $constrained, $outsideShift, $greyed, theme }) =>
        $isOver        ? `${theme.btn.primary}22` :
        $constrained   ? `${theme.btn.primary}18` :
        $outsideShift  ? `${theme.btn.danger}28` :
        $greyed        ? theme.area.highBg :
        'transparent'
    };
    cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
    transition: background 0.1s;
    flex-shrink: 0;
`

const UnavailableZonesLayer = styled.div`
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    pointer-events: none;
    z-index: 2;
`

const UnavailableZone = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${({ $left }) => $left}px;
    width: ${({ $width }) => $width}px;
    background: ${({ theme }) => `${theme.btn.danger}22`};
    border-left: 2px solid ${({ theme }) => `${theme.btn.danger}60`};
    display: flex;
    align-items: center;
    justify-content: space-around;
    overflow: hidden;
    pointer-events: none;
`

const UnavailableLabel = styled.span`
    font-size: var(--font-XS);
    font-weight: 600;
    color: ${({ theme }) => theme.btn.danger};
    opacity: 0.55;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    user-select: none;
`

const BlocksLayer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 1;
`

const Block = styled.div`
    position: absolute;
    top: 5px;
    height: calc(100% - 10px);
    left: calc(${({ $left }) => $left}px + 2px);
    width: calc(${({ $width }) => $width}px - 4px);
    background: ${({ theme, $moving, $foreign }) =>
        $moving   ? `${theme.btn.primary}18` :
        $foreign  ? theme.area.highBg :
        theme.card.background
    };
    border-radius: 0.4rem;
    border: 1.5px solid ${({ theme, $moving, $foreign }) =>
        $moving  ? theme.btn.primary :
        $foreign ? theme.area.border :
        theme.area.lightBorder
    };
    border-left: 3px solid ${({ theme, $moving, $foreign }) =>
        $moving  ? theme.btn.primary :
        $foreign ? theme.area.border :
        theme.area.lightBorder
    };
    box-shadow: ${({ theme, $moving }) => $moving
        ? `0 0 0 3px ${theme.btn.primary}33, 0 2px 8px rgba(0,0,0,0.12)`
        : '0 1px 3px rgba(0,0,0,0.07)'
    };
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 0.45rem 0 0.4rem;
    overflow: hidden;
    pointer-events: ${({ $foreign }) => $foreign ? 'none' : 'auto'};
    cursor: ${({ $foreign }) => $foreign ? 'default' : 'grab'};
    opacity: ${({ $dragging, $foreign }) => $dragging ? 0.35 : $foreign ? 0.45 : 1};
    transition: box-shadow 0.15s, border-color 0.15s, background 0.15s, opacity 0.1s;

    &:active { cursor: ${({ $foreign }) => $foreign ? 'default' : 'grabbing'}; }
`

const BlockName = styled.span`
    font-size: var(--font-XS);
    font-weight: 600;
    color: ${({ theme }) => theme.font.color};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
`

const BlockSub = styled.span`
    font-size: var(--font-XS);
    color: ${({ theme }) => theme.font.label};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
    margin-top: 1px;
`

const InfoBtn = styled.button`
    position: absolute;
    bottom: 3px;
    right: 4px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    line-height: 1;
    color: ${({ theme }) => theme.font.label};
    opacity: 0.6;
    transition: opacity 0.15s, color 0.15s;
    pointer-events: auto;

    &:hover {
        opacity: 1;
        color: ${({ theme }) => theme.btn.primary};
    }
`

const ConstraintLine = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${({ $left }) => $left}px;
    width: 2px;
    background: ${({ theme }) => theme.btn.danger};
    z-index: 2;
    pointer-events: none;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -4px;
        width: 10px;
        height: 10px;
        background: ${({ theme }) => theme.btn.danger};
        border-radius: 50%;
    }
`

// ── Droppable cell ─────────────────────────────────────────────────────────

const DroppableCell = ({ id, data, disabled, greyed, constrained, outsideShift, isHour, isFirst, isSnapped }) => {
    const { isOver, setNodeRef } = useDroppable({ id, data, disabled })
    const highlighted = isSnapped != null ? isSnapped : (isOver && !disabled)
    return (
        <Cell
            ref={setNodeRef}
            $isOver={highlighted}
            $disabled={disabled}
            $greyed={greyed}
            $constrained={constrained}
            $outsideShift={outsideShift}
            $isHour={isHour}
            $isFirst={isFirst}
        />
    )
}

// ── Draggable block ────────────────────────────────────────────────────────

const DraggableBlock = ({ booking, left, width, isMoving, onSelect, onInfo, children }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `board::${booking.id}`,
        data: { op: booking }
    })
    return (
        <Block
            ref={setNodeRef}
            $left={left}
            $width={width}
            $moving={isMoving}
            $dragging={isDragging}
            onClick={() => onSelect?.(booking.id)}
            {...listeners}
            {...attributes}
        >
            {children}
            {onInfo && (
                <InfoBtn
                    onClick={(e) => { e.stopPropagation(); onInfo(booking) }}
                    onPointerDown={(e) => e.stopPropagation()}
                    title="Details"
                >
                    <MdInfoOutline />
                </InfoBtn>
            )}
        </Block>
    )
}

// ── Time math helpers ──────────────────────────────────────────────────────

const addMinutes = (date, m) => new Date(date.getTime() + m * 60000)

const slotToLeft = (slotTime, shiftStart, slotMinutes) => {
    const diffMs = slotTime.getTime() - shiftStart.getTime()
    return (diffMs / (slotMinutes * 60000)) * SLOT_WIDTH
}

const durationToWidth = (startMs, endMs, slotMinutes) => {
    const diffMs = endMs - startMs
    return (diffMs / (slotMinutes * 60000)) * SLOT_WIDTH
}

// ── Main component ─────────────────────────────────────────────────────────

const WorkPlannerSwimlane = ({ resource, isEligible, currentOpIds, onSelectOp, onInfo, slots, bookings, earliestStart, shiftStart, shiftEnd, slotMinutes, snapPreview, selectedOp, effectiveShift, fieldConfig, labelConfig }) => {
    const { t } = useTranslation()
    const wcBookings = bookings[resource.id] ?? []
    const { active: dndActive, over: dndOver } = useDndContext()
    const isDragActive = !!dndActive
    const isThisWcActive = dndOver?.data.current?.resource?.id === resource.id
    const snapPreviewMs = isThisWcActive && snapPreview ? snapPreview.getTime() : null

    const boardDate = useMemo(() => {
        const d = new Date(shiftStart)
        d.setHours(0, 0, 0, 0)
        return d
    }, [shiftStart])

    const unavailableZones = useMemo(() => {
        // When activeDays is set and today is not in it, mark the entire row as unavailable
        const isoDay = boardDate.getDay() === 0 ? 7 : boardDate.getDay()
        const activeDays = effectiveShift?.activeDays ?? []
        const dayIsActive = !activeDays.length || activeDays.includes(isoDay)
        if (!dayIsActive && slots.length > 0) {
            return [{ start: slots[0], end: addMinutes(slots[slots.length - 1], slotMinutes) }]
        }
        return computeUnavailableZones(slots, effectiveShift?.shiftSlots ?? null, boardDate, slotMinutes)
    }, [slots, effectiveShift, boardDate, slotMinutes])

    const resLabelField = fieldConfig?.resourceLabel    ?? 'name'
    const resSubField   = fieldConfig?.resourceSubLabel ?? 'code'
    const seqField      = fieldConfig?.sequence         ?? 'sequence'
    const labelField    = fieldConfig?.taskLabel        ?? 'name'
    const subField      = fieldConfig?.taskSubLabel
    const startField    = fieldConfig?.startTime        ?? 'plannedStart'
    const endField      = fieldConfig?.endTime          ?? 'plannedEnd'

    // Constraint line position (px from left edge of grid)
    const constraintLeft = earliestStart && isEligible
        ? slotToLeft(earliestStart, shiftStart, slotMinutes)
        : null

    return (
        <Row>
            <SwimlaneLabel>
                <WcName>{resource[resLabelField]}</WcName>
                {resource[resSubField] && <WcMeta>{resource[resSubField]}</WcMeta>}
            </SwimlaneLabel>

            <GridArea>
                <CellWrapper>
                    {slots.map((slot, i) => {
                        const isPastShift   = shiftEnd != null && slot >= shiftEnd
                        const isConstrained = isEligible && earliestStart != null && slot < earliestStart
                        const isDisabled    = !isEligible || isConstrained ||
                            (!selectedOp && !isDragActive)
                        const isOutsideShift = unavailableZones.some(z => slot >= z.start && slot < z.end)
                        // Grey buffer cells only when no shift is configured (fallback behaviour)
                        const isGreyed      = effectiveShift == null && isPastShift
                        const isHour        = slot.getMinutes() === 0

                        return (
                            <DroppableCell
                                key={slot.getTime()}
                                id={`${resource.id}::${slot.getTime()}`}
                                data={{ resource, slotTime: slot }}
                                disabled={isDisabled}
                                greyed={isGreyed}
                                constrained={isConstrained}
                                outsideShift={isOutsideShift}
                                isHour={isHour}
                                isFirst={i === 0}
                                isSnapped={snapPreviewMs != null ? slot.getTime() === snapPreviewMs : null}
                            />
                        )
                    })}
                </CellWrapper>

                {/* Unavailable shift zones */}
                {unavailableZones.length > 0 && (
                    <UnavailableZonesLayer>
                        {unavailableZones.map((zone, i) => {
                            const left  = slotToLeft(zone.start, shiftStart, slotMinutes)
                            const width = durationToWidth(zone.start.getTime(), zone.end.getTime(), slotMinutes)
                            const labelCount = width >= 60 ? Math.max(1, Math.floor(width / 160)) : 0
                            return (
                                <UnavailableZone key={i} $left={left} $width={width}>
                                    {Array.from({ length: labelCount }, (_, li) => (
                                        <UnavailableLabel key={li}>{` ${t(labelConfig?.unavailable ?? 'all.unavailable')} `}</UnavailableLabel>
                                    ))}
                                </UnavailableZone>
                            )
                        })}
                    </UnavailableZonesLayer>
                )}

                {/* Constraint line */}
                {constraintLeft != null && constraintLeft >= 0 && constraintLeft <= slots.length * SLOT_WIDTH && (
                    <ConstraintLine $left={constraintLeft} />
                )}

                {/* Booking blocks */}
                <BlocksLayer>
                    {wcBookings.map((booking) => {
                        if (!booking[startField] || !booking[endField]) return null
                        const bStart = new Date(booking[startField])
                        const bEnd   = new Date(booking[endField])

                        // Clamp start to visible window; let end extend into buffer
                        const visStart = bStart < shiftStart ? shiftStart : bStart
                        const visEnd   = bEnd
                        if (visStart >= visEnd) return null

                        const left  = slotToLeft(visStart, shiftStart, slotMinutes)
                        const width = durationToWidth(visStart.getTime(), visEnd.getTime(), slotMinutes)
                        if (width <= 0) return null

                        // "moving" = this is the selected op's current block (being re-scheduled)
                        const isMoving = selectedOp != null && booking.id === selectedOp.id
                        // "foreign" = belongs to a different work order (context-only, not draggable)
                        const isForeign = currentOpIds != null && !currentOpIds.has(booking.id)

                        const blockContent = (
                            <>
                                <BlockName>
                                    {booking[seqField] != null ? `${booking[seqField]}. ` : ''}{booking[labelField]}
                                </BlockName>
                                {subField && booking[subField] && (
                                    <BlockSub>{booking[subField]}</BlockSub>
                                )}
                            </>
                        )

                        if (isForeign) {
                            return (
                                <Block
                                    key={booking.id}
                                    $left={left}
                                    $width={width}
                                    $foreign
                                >
                                    {blockContent}
                                </Block>
                            )
                        }

                        return (
                            <DraggableBlock
                                key={booking.id}
                                booking={booking}
                                left={left}
                                width={width}
                                isMoving={isMoving}
                                onSelect={onSelectOp}
                                onInfo={onInfo}
                            >
                                {blockContent}
                            </DraggableBlock>
                        )
                    })}
                </BlocksLayer>
            </GridArea>
        </Row>
    )
}

export { SLOT_WIDTH, LABEL_WIDTH }
export default WorkPlannerSwimlane
